import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { OWNER_CONTACT } from "../lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/properties", label: "Properties" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#121212]/70 border-b border-[#F5F5DC]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-2 group"
        >
          <span
            className="font-serif-luxe text-2xl md:text-3xl tracking-tight"
            style={{ color: "var(--vs-text)" }}
          >
            VacayStay
          </span>
          <span
            className="font-serif-luxe text-2xl md:text-3xl italic"
            style={{ color: "var(--vs-gold)" }}
          >
            Rentals
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide uppercase transition-colors ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-[#F5F5DC] hover:text-[#D4AF37]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:+${OWNER_CONTACT.whatsapp}`}
            data-testid="nav-call-btn"
            className="flex items-center gap-2 text-sm text-[#F5F5DC]/80 hover:text-[#D4AF37] transition-colors"
          >
            <Phone size={14} />
            {OWNER_CONTACT.phone}
          </a>
          <button
            onClick={() => nav("/contact")}
            data-testid="nav-book-btn"
            className="px-5 py-2.5 text-xs tracking-[0.2em] uppercase bg-[#D4AF37] text-[#121212] hover:bg-[#B3922E] transition-all hover:translate-y-[-2px]"
          >
            Book Now
          </button>
        </div>

        <button
          className="md:hidden text-[#F5F5DC]"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#121212] border-t border-[#F5F5DC]/10 px-6 py-6 space-y-4"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className="block text-sm uppercase tracking-wide text-[#F5F5DC] hover:text-[#D4AF37]"
            >
              {l.label}
            </NavLink>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              nav("/contact");
            }}
            className="w-full mt-4 px-5 py-3 text-xs tracking-[0.2em] uppercase bg-[#D4AF37] text-[#121212]"
            data-testid="mobile-book-btn"
          >
            Book Now
          </button>
        </div>
      )}
    </header>
  );
}
